// src/services/purchaseApi.js
// API para compras: crear factura (solo totales), listar, detalle, estados, términos, reportes

import { dotoniwoofApi } from '../api/dotoniwooApi';

const BASE = '/purchase.php';

/** Extrae un total numérico de la respuesta del backend (formato según documentación) */
function parseReportTotal(data, field) {
  if (!data) return 0;
  const msg = data.message;
  if (typeof msg !== 'object' || msg == null) {
    const num = typeof msg === 'number' ? msg : parseFloat(msg);
    return Number.isFinite(num) ? num : 0;
  }
  // Según documentación: total_purchases | total_pending | total_overdue
  const val = field ? msg[field] : msg.total_purchases ?? msg.total_pending ?? msg.total_overdue ?? msg.total ?? msg.total_amount ?? msg.amount ?? msg.grand_total;
  const num = typeof val === 'string' ? parseFloat(val) : Number(val);
  return Number.isFinite(num) ? num : 0;
}

const isSuccess = (code) => code === 200 || code === '200';

/**
 * Listar términos de crédito
 * GET termsType.php
 */
export async function getTermsTypes() {
  try {
    const res = await dotoniwoofApi.get('/termsType.php');
    const data = res.data;
    if (data?.code === 200) {
      return { ok: true, data: data.message || [] };
    }
    return { ok: false, data: [] };
  } catch (error) {
    console.error('getTermsTypes error', error);
    return { ok: false, data: [] };
  }
}

/**
 * Obtener estados de pago para compras (PENDIENTE_DE_PAGO, PAGADA)
 * GET status.php/COMPRAS
 */
export async function getStatusCompras() {
  try {
    const res = await dotoniwoofApi.get('/status.php/COMPRAS');
    const data = res.data;
    if (data?.code === 200 && Array.isArray(data.message)) {
      return { ok: true, data: data.message };
    }
    return { ok: false, data: [] };
  } catch (error) {
    console.error('getStatusCompras error', error);
    return { ok: false, data: [] };
  }
}

/**
 * Marcar compra como pagada
 * PUT purchase.php/{id}/status
 * @param {number} id - id_purchase
 * @param {Object} payload - { firebase_UID, id_status }
 */
export async function updatePurchaseStatus(id, payload) {
  try {
    const res = await dotoniwoofApi.put(`${BASE}/${id}/status`, payload);
    const data = res.data;
    const status = data?.code ?? res.status;
    return { ok: status === 200, data, status };
  } catch (error) {
    console.error('updatePurchaseStatus error', error);
    const data = error.response?.data || { message: 'Error al actualizar estado' };
    return { ok: false, data };
  }
}

/**
 * Reporte de compras por mes del año
 * GET purchase.php/purchase-report?year=YYYY
 * Respuesta: { message: [{ months: ["January",...], purchases: [1200000,...] }] }
 */
export async function getPurchaseReport(year) {
  try {
    const res = await dotoniwoofApi.get(`${BASE}/purchase-report`, {
      params: { year: year || new Date().getFullYear() }
    });
    const data = res.data;
    if (isSuccess(data?.code) && Array.isArray(data?.message) && data.message[0]) {
      return {
        ok: true,
        months: data.message[0].months || [],
        purchases: Array.isArray(data.message[0].purchases) ? data.message[0].purchases : []
      };
    }
    return { ok: false, months: [], purchases: [] };
  } catch (error) {
    console.error('getPurchaseReport error', error);
    return { ok: false, months: [], purchases: [] };
  }
}

/**
 * Compras agrupadas por término de crédito
 * GET purchase.php/purchase-report-term?year=YYYY&month=M
 * Respuesta: { data: { data: [...], summary: { grand_total, total_purchases }, year, month } }
 */
export async function getPurchaseReportTerm(year, month) {
  try {
    const params = {};
    if (year) params.year = year;
    if (month) params.month = month;
    const res = await dotoniwoofApi.get(`${BASE}/purchase-report-term`, { params });
    const data = res.data;
    if (isSuccess(data?.code) && data?.data) {
      const d = data.data;
      return {
        ok: true,
        items: Array.isArray(d.data) ? d.data : [],
        summary: d.summary || { grand_total: 0, total_purchases: 0 },
        year: d.year,
        month: d.month
      };
    }
    return { ok: false, items: [], summary: { grand_total: 0, total_purchases: 0 } };
  } catch (error) {
    console.error('getPurchaseReportTerm error', error);
    return { ok: false, items: [], summary: { grand_total: 0, total_purchases: 0 } };
  }
}

/**
 * Total comprado en el mes
 * GET purchase.php?purchase-report-month&year=YYYY&month=M
 */
export async function getPurchaseReportMonth(year, month) {
  try {
    const res = await dotoniwoofApi.get(BASE, {
      params: { 'purchase-report-month': 1, year, month }
    });
    const data = res.data;
    if (isSuccess(data?.code)) {
      return { ok: true, total: parseReportTotal(data, 'total_purchases') };
    }
    return { ok: false, total: 0 };
  } catch (error) {
    console.error('getPurchaseReportMonth error', error);
    return { ok: false, total: 0 };
  }
}

/**
 * Compras pendientes que vencen en los próximos N días
 * GET purchase.php/purchase-report-due-soon?days=7
 */
export async function getPurchaseReportDueSoon(days = 7) {
  try {
    const params = days ? { days: Math.min(days, 90) } : {};
    const res = await dotoniwoofApi.get(`${BASE}/purchase-report-due-soon`, { params });
    const data = res.data;
    if (isSuccess(data?.code) && data?.data) {
      const d = data.data;
      return {
        ok: true,
        purchases: Array.isArray(d.purchases) ? d.purchases : [],
        summary: d.summary || { total_amount: 0, count: 0, days_window: 7 }
      };
    }
    return { ok: false, purchases: [], summary: { total_amount: 0, count: 0, days_window: 7 } };
  } catch (error) {
    console.error('getPurchaseReportDueSoon error', error);
    return { ok: false, purchases: [], summary: { total_amount: 0, count: 0, days_window: 7 } };
  }
}

/**
 * Total pendiente por pagar
 * GET purchase.php?purchase-report-pending
 */
export async function getPurchaseReportPending() {
  try {
    const res = await dotoniwoofApi.get(BASE, { params: { 'purchase-report-pending': 1 } });
    const data = res.data;
    if (isSuccess(data?.code)) {
      return { ok: true, total: parseReportTotal(data, 'total_pending') };
    }
    return { ok: false, total: 0 };
  } catch (error) {
    console.error('getPurchaseReportPending error', error);
    return { ok: false, total: 0 };
  }
}

/**
 * Total vencido
 * GET purchase.php?purchase-report-overdue
 */
export async function getPurchaseReportOverdue() {
  try {
    const res = await dotoniwoofApi.get(BASE, { params: { 'purchase-report-overdue': 1 } });
    const data = res.data;
    if (isSuccess(data?.code)) {
      return { ok: true, total: parseReportTotal(data, 'total_overdue') };
    }
    return { ok: false, total: 0 };
  } catch (error) {
    console.error('getPurchaseReportOverdue error', error);
    return { ok: false, total: 0 };
  }
}

/**
 * Crear factura de compra (solo totales, sin ítems)
 * POST purchase.php
 * @param {Object} payload - { firebase_UID, date, supplierId, paymentMethod, subtotal, grand_total, total_discount?, referenceDoc?, notes?, termsType?, taxes?[] }
 */
export async function createPurchase(payload) {
  try {
    const res = await dotoniwoofApi.post(BASE, payload);
    const data = res.data;
    const status = data?.code ?? res.status;
    return { ok: true, status, data };
  } catch (error) {
    console.error('createPurchase error', error);
    const status = error.response?.status || 500;
    const data = error.response?.data || { message: 'Error de red o inesperado' };
    return { ok: false, status, data };
  }
}

/**
 * Actualizar factura de compra
 * PUT purchase.php/{id}
 * @param {number} id - id_purchase
 * @param {Object} payload - { firebase_UID, date, supplierId, paymentMethod, subtotal, grand_total, total_discount?, referenceDoc?, notes?, termsType?, taxes?[] }
 */
export async function updatePurchase(id, payload) {
  try {
    const res = await dotoniwoofApi.put(`${BASE}/${id}`, payload);
    const data = res.data;
    const status = data?.code ?? res.status;
    return { ok: status === 200 || status === 201, status, data };
  } catch (error) {
    console.error('updatePurchase error', error);
    const status = error.response?.status || 500;
    const data = error.response?.data || { message: 'Error de red o inesperado' };
    return { ok: false, status, data };
  }
}

/**
 * Listar compras (paginado, con filtros opcionales)
 * GET purchase.php?page=1&limit=10&supplier_id=&date_from=&date_to=&payment_method_id=&status=&overdue=
 */
export async function listPurchases(page = 1, limit = 10, filters = {}) {
  try {
    const params = { page, limit };
    if (filters.supplier_id) params.supplier_id = filters.supplier_id;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.payment_method_id) params.payment_method_id = filters.payment_method_id;
    if (filters.status) params.status = filters.status;
    if (filters.overdue === true || filters.overdue === 1) params.overdue = 1;
    const res = await dotoniwoofApi.get(BASE, { params });
    const data = res.data;
    if (data?.code === 200) {
      return {
        ok: true,
        total_rows: data.total_rows ?? 0,
        total_pages: data.total_pages ?? 1,
        items: data.message ?? []
      };
    }
    return { ok: false, items: [], total_rows: 0, total_pages: 0 };
  } catch (error) {
    console.error('listPurchases error', error);
    return { ok: false, items: [], total_rows: 0, total_pages: 0 };
  }
}

/**
 * Obtener detalle de compra (factura + impuestos + recepciones)
 * GET purchase.php/{id}/detail
 */
export async function getPurchaseDetail(id) {
  try {
    const res = await dotoniwoofApi.get(`${BASE}/${id}/detail`);
    const data = res.data;
    if (data?.code === 200 && data?.message) {
      return { ok: true, data: data.message };
    }
    return { ok: false, data: null };
  } catch (error) {
    console.error('getPurchaseDetail error', error);
    return { ok: false, data: null };
  }
}

/**
 * Reporte de compras por mes con todos los detalles (impuestos, stock, etc.)
 * GET purchase.php/purchase-report-by-month?year=YYYY&month=M&page=1&limit=20
 * @param {number} year - Año (opcional, por defecto año actual)
 * @param {number} month - Mes 1-12 (opcional, por defecto mes actual)
 * @param {number} page - Número de página (opcional, por defecto 1)
 * @param {number} limit - Resultados por página (opcional, por defecto 20, máximo 100)
 */
export async function getPurchaseReportByMonth(year, month, page = 1, limit = 20) {
  try {
    const params = {};
    if (year) params.year = year;
    if (month) params.month = month;
    if (page) params.page = page;
    if (limit) params.limit = Math.min(limit, 100); // Máximo 100
    
    const res = await dotoniwoofApi.get(`${BASE}/purchase-report-by-month`, { params });
    const data = res.data;
    
    if (isSuccess(data?.code) && Array.isArray(data?.data)) {
      return {
        ok: true,
        year: data.year,
        month: data.month,
        total_rows: data.total_rows || 0,
        total_pages: data.total_pages || 1,
        page: data.page || page,
        limit: data.limit || limit,
        items: data.data
      };
    }
    return {
      ok: false,
      year: year || new Date().getFullYear(),
      month: month || new Date().getMonth() + 1,
      total_rows: 0,
      total_pages: 0,
      page: 1,
      limit: limit,
      items: []
    };
  } catch (error) {
    console.error('getPurchaseReportByMonth error', error);
    return {
      ok: false,
      year: year || new Date().getFullYear(),
      month: month || new Date().getMonth() + 1,
      total_rows: 0,
      total_pages: 0,
      page: 1,
      limit: limit,
      items: []
    };
  }
}
